import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, AlertCircle, ChevronDown, ChevronUp, Paperclip, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';

// TB-24.2 — UnifiedTimeline (read-only) sobre v_activity_stream v2.
// Aceita entityType='empresa'|'oferta' e filtra pela coluna correspondente.
// Sem ações de mutação: nenhum botão de approve/enrich/edit.

const CHANNEL_META = {
  email:           { emoji: '📧', label: 'email',     color: 'bg-blue-50 text-blue-700 border-blue-200' },
  whatsapp:        { emoji: '💬', label: 'whatsapp',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  phone:           { emoji: '📞', label: 'chamada',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  document_intake: { emoji: '📄', label: 'documento', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  calendar:        { emoji: '📅', label: 'agenda',    color: 'bg-pink-50 text-pink-700 border-pink-200' },
  internal_system: { emoji: '⚙️', label: 'sistema',   color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const DEFAULT_META = { emoji: '📌', label: 'evento', color: 'bg-slate-50 text-slate-600 border-slate-200' };

function getChannelMeta(channel) {
  return CHANNEL_META[channel] || DEFAULT_META;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function fmtDayLabel(isoString) {
  if (!isoString) return 'Sem data';
  const d = new Date(isoString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(d, today)) return 'Hoje';
  if (isSameDay(d, yesterday)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' });
}

function fmtTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function dayKey(isoString) {
  return isoString ? isoString.slice(0, 10) : 'sem-data';
}

function groupByDay(events) {
  const groups = [];
  let lastKey = null;
  let bucket = null;
  for (const ev of events) {
    const key = dayKey(ev.event_at);
    if (key !== lastKey) {
      bucket = { key, label: fmtDayLabel(ev.event_at), items: [] };
      groups.push(bucket);
      lastKey = key;
    }
    bucket.items.push(ev);
  }
  return groups;
}

const DETAIL_TRUNCATE = 160;

export default function UnifiedTimeline({ entityType, entityId, limit = 20 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [reloadKey, setReloadKey] = useState(0);

  const filterCol = entityType === 'oferta' ? 'oferta_id' : 'empresa_id';

  const fetchPage = useCallback(async (offset) => {
    const { data, error: err } = await supabase
      .from('v_activity_stream')
      .select('*')
      .eq(filterCol, entityId)
      .order('event_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (err) throw err;
    return data || [];
  }, [entityId, filterCol, limit]);

  useEffect(() => {
    if (!entityId) {
      setLoading(false);
      setEvents([]);
      setHasMore(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError(null);
    setEvents([]);
    setExpanded(new Set());
    setHasMore(true);
    fetchPage(0)
      .then((rows) => {
        if (!active) return;
        setEvents(rows);
        setHasMore(rows.length === limit);
      })
      .catch((e) => { if (active) setError(e.message || String(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [entityId, fetchPage, limit, reloadKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const rows = await fetchPage(events.length);
      setEvents((prev) => [...prev, ...rows]);
      setHasMore(rows.length === limit);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingMore(false);
    }
  }, [events.length, fetchPage, limit, hasMore, loadingMore]);

  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const groups = useMemo(() => groupByDay(events), [events]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
        <Loader2 size={14} className="animate-spin" /> Carregando timeline...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-1.5">
        <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div>{error}</div>
          <button
            type="button"
            onClick={reload}
            className="mt-1 text-[10px] text-red-700 underline hover:no-underline"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!entityId || events.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic py-6 text-center">
        Nenhum evento registrado para esta {entityType === 'oferta' ? 'oferta' : 'empresa'}.
      </div>
    );
  }

  return (
    <div className="space-y-3" aria-label="Timeline de atividades">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          {events.length} {events.length === 1 ? 'evento' : 'eventos'}{hasMore ? '+' : ''}
        </div>
        <button
          type="button"
          onClick={reload}
          className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          title="Recarregar"
        >
          <RefreshCw size={10} /> Atualizar
        </button>
      </div>

      {groups.map((g) => (
        <div key={g.key}>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 sticky top-0 bg-white/95 backdrop-blur py-1 z-[1]">
            {g.label}
          </div>
          <ol className="relative space-y-2 pl-4 border-l border-slate-200">
            {g.items.map((ev) => {
              const meta = getChannelMeta(ev.channel);
              const key = ev.event_key || `${ev.source_table}-${ev.source_id}-${ev.event_at}`;
              const detail = ev.detail || '';
              const isExpanded = expanded.has(key);
              const truncate = detail.length > DETAIL_TRUNCATE;
              const shown = truncate && !isExpanded ? `${detail.slice(0, DETAIL_TRUNCATE).trim()}…` : detail;
              const artifactCount = typeof ev.artifact_count === 'number' ? ev.artifact_count : 0;
              const hasArtifacts = ev.has_artifacts === true || artifactCount > 0;

              return (
                <li key={key} className="relative">
                  <span
                    className="absolute -left-[19px] top-2 inline-block h-2.5 w-2.5 rounded-full bg-white border border-slate-300"
                    aria-hidden="true"
                  />
                  <div className="bg-white border border-slate-200 rounded p-2 text-xs">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="text-sm leading-none" aria-hidden="true">{meta.emoji}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] rounded border ${meta.color}`}>
                        {meta.label}
                      </span>
                      {ev.actor_type === 'ai' && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                          🤖 IA
                        </span>
                      )}
                      {hasArtifacts && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-0.5">
                          <Paperclip size={9} /> {artifactCount || ''}{artifactCount ? ' ' : ''}{artifactCount === 1 ? 'artefato' : 'artefatos'}
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap tabular-nums">
                        {fmtTime(ev.event_at)}
                      </span>
                    </div>
                    {ev.title && (
                      <div className="font-semibold text-slate-800 mb-0.5 break-words" title={ev.title}>
                        {ev.title}
                      </div>
                    )}
                    {detail && (
                      <div className="text-slate-600 whitespace-pre-wrap break-words leading-snug">
                        {shown}
                      </div>
                    )}
                    {truncate && (
                      <button
                        type="button"
                        onClick={() => toggleExpand(key)}
                        className="mt-1 text-[10px] text-blue-600 hover:underline inline-flex items-center gap-0.5"
                      >
                        {isExpanded
                          ? (<><ChevronUp size={10} /> Recolher</>)
                          : (<><ChevronDown size={10} /> Ler mais</>)}
                      </button>
                    )}
                    {ev.actor_id && (
                      <div className="text-[10px] text-slate-400 mt-1 truncate" title={ev.actor_id}>
                        {ev.actor_id}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}

      {hasMore && (
        <div className="pt-1 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {loadingMore && <Loader2 size={12} className="animate-spin" />}
            {loadingMore ? 'Carregando...' : 'Carregar mais'}
          </button>
        </div>
      )}
      {!hasMore && events.length > limit && (
        <div className="text-[10px] text-slate-400 text-center italic pt-1">
          Fim do histórico.
        </div>
      )}
    </div>
  );
}
